import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPublishedGame, sendRun } from '../api/playAPI';
import { getGuestSessionId } from '../../utils/guestSession';
import MissionGame from './MissionGame';
import './GameBuilder.css';

/**
 * A published game, opened from its share link (/play/<token>) - no account needed. The player may give a first
 * name for the trainer's report; each play is sent when it ends (won, lost, or left part way) with a random id this
 * browser tab made up (utils/guestSession.js), never anything that identifies them.
 *
 * Opened by a SCORM package inside an LMS (?lms=scorm, backend services/scorm.py), the name is the LMS's (?learner=) and
 * each play's end is also posted to the package's page, which reports passed / failed to the LMS.
 *
 * The game is a page of its own (?play=1), not a window over this one: only the game is on the page while it's played,
 * the browser's Back leaves it, and closing it comes back here.
 */
export const RUN_MESSAGE = 'simulatejob:run';
// the first name given, kept for this tab - so a reload while playing still reports it (storage may be blocked: then not)
const nameKey = (token) => `simulatejob:guest-name:${token}`;
const remembered = (token) => { try { return sessionStorage.getItem(nameKey(token)) || ''; } catch { return ''; } };
const remember = (token, v) => { try { sessionStorage.setItem(nameKey(token), v); } catch { /* not kept */ } };
export default function GuestPlay() {
  const { token } = useParams();
  const [search, setSearch] = useSearchParams();
  const lms = search.get('lms') === 'scorm';
  const learner = lms ? (search.get('learner') || '').trim().slice(0, 40) : '';
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [name, setNameNow] = useState(() => learner || remembered(token));
  const playing = search.get('play') === '1';
  const [plays, setPlays] = useState(0);
  const setName = (v) => { setNameNow(v); remember(token, v); };
  const withPlay = (on) => {
    const next = new URLSearchParams(search);
    if (on) next.set('play', '1'); else next.delete('play');
    return next;
  };
  const startPlay = () => setSearch(withPlay(true));
  const stopPlay = () => setSearch(withPlay(false), { replace: true });

  useEffect(() => {
    let live = true;
    getPublishedGame(token).then((g) => live && setGame(g)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [token]);

  const onRun = (run) => {
    setPlays((n) => n + 1);
    sendRun(token, { ...run, guest: getGuestSessionId() || '', name: name.trim(), version: game.version });
    if (lms && window.parent !== window) {
      window.parent.postMessage({ type: RUN_MESSAGE, outcome: run.outcome, seconds: run.seconds, version: game.version }, '*');
    }
  };

  if (error) return <main className="gb-guest"><div className="gb-panel" role="alert"><h2>Simulation not found</h2><p>{error}</p></div></main>;
  if (!game) return <main className="gb-guest"><p className="gb-muted">Loading the simulation…</p></main>;
  if (playing) return <MissionGame spec={game.spec} page onClose={stopPlay} onRun={onRun} />;
  return (
    <main className="gb-guest">
      <div className="gb-panel">
        <h2>{game.title || game.spec.mission.name}</h2>
        <p>{game.spec.objective.join(' ')}</p>
        {learner ? <p className="gb-small">Playing as <strong>{learner}</strong> - your result goes to your learning system.</p> : (
          <label className="gb-flow-row">
            <span>Your first name <span className="gb-muted">(optional - only your trainer sees it)</span></span>
            <input type="text" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} aria-label="Your first name" />
          </label>
        )}
        <div className="gb-actions">
          <button type="button" className="gb-btn" onClick={startPlay}>▶ {plays ? 'Play again' : 'Start'}</button>
        </div>
        <p className="gb-small gb-muted">No account needed. How you play (what you did, how long it took) is shared with whoever made this simulation.</p>
      </div>
    </main>
  );
}
